// T6 (LAUNCH_PLAN.md) — client-side consent helpers.
//
// CONSENT_VERSION must match functions-v2/lib/consent_v2.js exactly. Bumping
// the version requires a coordinated change in both files + a redeploy of
// the recordConsent_v2 function + a frontend redeploy. See:
//   src/admin/_docs/consent-versioning.md

import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { db, functions } from './firebase';

export const CONSENT_VERSION = '2026-05';

export type ConsentSource =
  | 'email_signup'
  | 'google_signup_completion'
  | 'consent_modal_existing_user';

export interface ConsentDoc {
  version: string;
  userId: string;
  acceptedAt: unknown; // Firestore Timestamp
  source: ConsentSource;
  userAgentHash: string | null;
  policyTexts: { terms: string; privacy: string };
}

/**
 * Returns true if the given uid has a consent doc for the current
 * CONSENT_VERSION. Failures (network, permission-denied) resolve to false
 * — caller can decide whether to retry or block the user.
 */
export async function hasConsentForCurrentVersion(uid: string): Promise<boolean> {
  if (!uid) return false;
  try {
    const snap = await getDoc(doc(db, 'users', uid, 'consents', CONSENT_VERSION));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Calls the recordConsent_v2 Cloud Function. Throws on failure (caller
 * decides retry strategy — typically a "Try again" button in UI).
 */
export async function recordConsent(source: ConsentSource): Promise<void> {
  const fn = httpsCallable<
    { version: string; source: ConsentSource },
    { ok: boolean; version: string }
  >(functions, 'recordConsent_v2');
  const res: HttpsCallableResult<{ ok: boolean; version: string }> = await fn({
    version: CONSENT_VERSION,
    source,
  });
  if (!res.data?.ok) {
    throw new Error('recordConsent_v2 returned non-ok response');
  }
}
